import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { destroy } from 'redux-form';
import { matchPath, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  ACKNOWLEDGED,
  APPOINTMENT_SCHEDULED,
  APPROVED,
  AUTHORIZATIONS,
  DEFAULT_AUTH_PATH,
  DEFAULT_UNAUTH_PATH,
  INITIATED,
  MOBILE_VERIFICATION_URL,
  NEW_USER,
  ON_HOLD,
  REACKNOWLEDGED,
  REGISTERED,
  MY_INFO_CALL_BACK_URL,
  MY_INFO_RESGISTRATION_URL,
  USER_REGISTRATION_URL,
  INDIVIDUAL,
  CORPORATE,
  SME_NEW_REGISTRATION_FLOW_COUNTRY_CODES,
  SME_NEW_REGISTRATION_FLOW_STATUSES,
  MY_INFO_REDIRECT_CONDITION,
  SSN_REKYC_PATH,
  HK,
  SG,
  AU,
  POST_LOGOUT_URL,
  TRANSFER_MONEY_URL,
  MULTIPLE_TRANSACTION_URL,
  HISTORY_URL,
  NEW_SME_URL,
  USER_PROFILE_URL,
  CONSENT_PERMISSION_URL,
  EMAIL_VERIFICATION_URL,
  HELP_CENTER,
  NIUM_FOREX_ESIGN_IN_CALLBACK_URL,
  RFI_URL,
  FORMNAME_RFI,
  APP_ONLY_CORRIDOOR_WELCOME_URL,
  NIUM_FOREX_URL,
  BASE_URL
} from '../../utils/constants';
import { showMessage } from '../../redux/actions/common-message/message-action';
import {
  authenticateUser,
  setIsEuRegion,
  setIsNewuser
} from '../../redux/actions/authentication/authentication-action';
import get from 'lodash/get';
import { isDynamicRouteMatch, isKYCSplitFlow } from '../../utils/library';
import { NF_ALLOWED_ROUTES } from '../../screens/nium-forex/root/screenConstants';
import { isAppOnlyCorridor } from '../../screens/app-only-corridor-welcome/utils';

function AuthRoute({
  component: Component,
  onlyUnAuth,
  is_user_authenticating,
  is_user_authenticated,
  userStatusReducer,
  user_session,
  isNiumForexUser,
  authenticateUser: authenticateUserAction,
  setIsNewuser,
  dispatch,
  featureState,
  ...rest
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isReady, setIsReady] = useState(false);

  // Initialize authentication on mount
  useEffect(() => {
    const initialize = async () => {
      // Removing documentId from nium-forex/es-callback?documentId=kWBAWyS for proper redirection
      if (location.pathname.includes(NIUM_FOREX_ESIGN_IN_CALLBACK_URL)) {
        navigate(NIUM_FOREX_ESIGN_IN_CALLBACK_URL, { replace: true });
      }

      if (onlyUnAuth) {
        if (!location.state?.twoFA && ![POST_LOGOUT_URL].includes(location.pathname)) {
          await authenticateUserAction(true);
        }
      } else {
        if (!(location.state?.twoFA || location.state?.isConsentForm)) {
          await authenticateUserAction();
        }
      }

      setIsReady(true);
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle RFI form cleanup when location changes
  useEffect(() => {
    if (!location.pathname?.includes('/rfi')) {
      dispatch(destroy(FORMNAME_RFI));
    }
  }, [location.pathname, dispatch]);

  const isAuthorized = (user_status, target_path, db_status, isNewKYCFLOw) => {
    const { account_type, sub_status, status, country_code } = userStatusReducer;
    let auth_route_fetch_status = user_status;
    if (isNewKYCFLOw) {
      auth_route_fetch_status = APPROVED;
    }
    let authorized_status_list = AUTHORIZATIONS[auth_route_fetch_status] || [];

    if (
      (status === INITIATED || status === ACKNOWLEDGED) &&
      sub_status === 'REKYC' &&
      SSN_REKYC_PATH.includes(target_path)
    ) {
      authorized_status_list.push(target_path);
    }
    if (
      country_code === 'SG' &&
      account_type === INDIVIDUAL &&
      MY_INFO_REDIRECT_CONDITION.includes(db_status)
    ) {
      authorized_status_list = [
        ...authorized_status_list,
        MY_INFO_CALL_BACK_URL,
        MY_INFO_RESGISTRATION_URL
      ];
    }
    if (account_type === CORPORATE) {
      authorized_status_list = [...authorized_status_list, HISTORY_URL, NEW_SME_URL];
    }
    /* matchPath required - as /acknowledgement/:payment_method/:status/:transaction_id
    path needs matching for dynamic segments like /acknowledgement/1324/approved/1198
     matchPath to check if target_path matches any pattern in authorized_status_list
     */
    const isMatched = authorized_status_list.some((pattern) => {
      // matchPath returns an object if matched, null if not
      return matchPath(pattern, target_path) !== null;
    });

    return isMatched;
  };

  const decisionMaker = () => {
    const { status, account_type, country_code } = userStatusReducer;
    let user_redirect_status = null;
    const target_path = location.pathname;
    const { is_new_user, isLoginOTPRequired, isLoginOTPVerified } = user_session;
    const otpDisabledSMECountries =
      account_type === CORPORATE && [HK, SG, AU].includes(country_code);

    /**
     * logic for- letting user continue new user flow if page not refreshed
     * is_new_user flag stored in user_session (redux)
     * is_new_user is set false if status is other than INITIATED/REGISTERED on login or user reaches ack screen
     */
    if (is_new_user === null && status) {
      // first time login / signup / refresh
      switch (status) {
        case INITIATED:
        case REGISTERED: {
          setIsNewuser(true);
          user_redirect_status = NEW_USER;
          break;
        }
        default: {
          setIsNewuser(false);
          user_redirect_status = status;
        }
      }
    } else if (is_new_user === true) {
      user_redirect_status = NEW_USER;
    } else if (is_new_user === false) {
      user_redirect_status = status;
    }

    if (target_path === USER_PROFILE_URL) user_redirect_status = status;

    const mobileVerificationStatus = [
      ACKNOWLEDGED,
      APPROVED,
      APPOINTMENT_SCHEDULED,
      REACKNOWLEDGED,
      ON_HOLD
    ];

    if (is_user_authenticating === false) {
      if (is_user_authenticated === true) {
        // If Singpass callback URL and we have SINGPASS_RFI_FLOW in LS, means its a RFI Singpass callback URL.
        if (target_path === MY_INFO_CALL_BACK_URL && localStorage.getItem('SINGPASS_RFI_FLOW')) {
          const queryString = window.location.search.slice(1);
          return (
            <Navigate
              to={`${RFI_URL}?${queryString}&rfiId=${localStorage.getItem('SINGPASS_RFI_FLOW')}`}
              replace
            />
          );
        }
        if (target_path === APP_ONLY_CORRIDOOR_WELCOME_URL) {
          return <Component {...rest} featureState={featureState} />;
        }

        if (isAppOnlyCorridor(account_type, country_code)) {
          return <Navigate to={APP_ONLY_CORRIDOOR_WELCOME_URL} replace />;
        }

        const { search, pathname } = location;
        const params = new URLSearchParams(search);
        const forceRedirection = get(location, 'state.forceRedirection', false);
        const redirectUrl = params.get('redirectUrl');
        const redirectionPaths = [USER_REGISTRATION_URL];
        const isSmeRegistrationFlow =
          SME_NEW_REGISTRATION_FLOW_COUNTRY_CODES.includes(country_code) &&
          account_type === CORPORATE;
        const isUserRegisteredOrInitiated = SME_NEW_REGISTRATION_FLOW_STATUSES.includes(status);
        const isDefaultAuthPath = [DEFAULT_AUTH_PATH].includes(target_path);
        const isNewKYCFlow =
          !forceRedirection &&
          !redirectionPaths.includes(pathname) &&
          isKYCSplitFlow(country_code, status, true, account_type);
        if (
          isAuthorized(user_redirect_status, target_path, status, isNewKYCFlow) ||
          (onlyUnAuth &&
            redirectUrl &&
            isAuthorized(user_redirect_status, redirectUrl, status, isNewKYCFlow))
        ) {
          if (onlyUnAuth) {
            //If already signin then send to dashboard
            if (redirectUrl) {
              // Ensure redirectUrl starts with '/' as for V6 '/' is needed for absolute paths
              const absoluteRedirectUrl = redirectUrl.startsWith('/')
                ? redirectUrl
                : `${BASE_URL}/${redirectUrl}`;
              return <Navigate to={absoluteRedirectUrl} state={{ from: location }} replace />;
            }
            // For nium forex - dashboard is /nium-forex path
            if (country_code === 'IN' && account_type === INDIVIDUAL && isNiumForexUser) {
              return <Navigate to={NIUM_FOREX_URL} state={{ from: location }} replace />;
            }

            return <Navigate to={DEFAULT_AUTH_PATH} state={{ from: location }} replace />;
          }

          if (
            country_code === 'SG' &&
            MY_INFO_REDIRECT_CONDITION.includes(status) &&
            account_type === INDIVIDUAL &&
            ![
              MY_INFO_RESGISTRATION_URL,
              MY_INFO_CALL_BACK_URL,
              USER_REGISTRATION_URL,
              USER_PROFILE_URL,
              HELP_CENTER
            ].includes(target_path) &&
            !isNewKYCFlow
          ) {
            return <Navigate to={MY_INFO_RESGISTRATION_URL} replace />;
          } else if (
            !otpDisabledSMECountries &&
            ((mobileVerificationStatus.includes(user_redirect_status) &&
              userStatusReducer.is_phone_verified === false) ||
              (isLoginOTPRequired && isLoginOTPVerified === false))
          ) {
            if (target_path !== MOBILE_VERIFICATION_URL) {
              return <Navigate to={MOBILE_VERIFICATION_URL} replace />;
            }
          } else {
            if (target_path === MOBILE_VERIFICATION_URL) {
              return <Navigate to={DEFAULT_AUTH_PATH} state={{ from: location }} replace />;
            } else if (isSmeRegistrationFlow && isUserRegisteredOrInitiated && !isDefaultAuthPath) {
              return <Navigate to={DEFAULT_AUTH_PATH} state={{ from: location }} replace />;
            } else if (pathname === TRANSFER_MONEY_URL && account_type === CORPORATE) {
              return <Navigate to={MULTIPLE_TRANSACTION_URL} replace />;
            } else if (pathname === MULTIPLE_TRANSACTION_URL && account_type === INDIVIDUAL) {
              return <Navigate to={TRANSFER_MONEY_URL} replace />;
            }
          }
          if (isDynamicRouteMatch(target_path, NF_ALLOWED_ROUTES)) {
            return <Component {...rest} featureState={featureState} />;
          }

          if (country_code === 'IN' && account_type === INDIVIDUAL && isNiumForexUser) {
            return <Navigate to={NIUM_FOREX_URL} state={{ from: location }} replace />;
          }
          return <Component {...rest} featureState={featureState} />;
        } else {
          if (isDynamicRouteMatch(target_path, NF_ALLOWED_ROUTES) && isNiumForexUser) {
            return <Component {...rest} featureState={featureState} />;
          }
          if (country_code === 'IN' && account_type === INDIVIDUAL && isNiumForexUser) {
            return <Navigate to={NIUM_FOREX_URL} state={{ from: location }} replace />;
          }

          return <Navigate to={DEFAULT_AUTH_PATH} state={{ from: location }} replace />;
        }
      } else {
        if (
          (target_path === CONSENT_PERMISSION_URL && location.state?.isConsentForm) ||
          (target_path === MOBILE_VERIFICATION_URL && user_session?.twoFactorTokenKey) ||
          target_path === EMAIL_VERIFICATION_URL ||
          onlyUnAuth
        ) {
          return <Component {...rest} featureState={featureState} />;
        }

        return <Navigate to={DEFAULT_UNAUTH_PATH} state={{ from: location }} replace />;
      }
    } else {
      return null;
    }
  };

  if (!isReady) {
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {decisionMaker()}
    </>
  );
}

AuthRoute.propTypes = {
  component: PropTypes.any,
  is_user_authenticating: PropTypes.bool,
  is_user_authenticated: PropTypes.bool,
  userStatusReducer: PropTypes.object,
  onlyUnAuth: PropTypes.bool,
  showMessage: PropTypes.func,
  authenticateUser: PropTypes.func,
  setIsNewuser: PropTypes.func,
  user_session: PropTypes.object,
  setIsEuRegion: PropTypes.func,
  isNewUIEnabled: PropTypes.bool,
  isNiumForexUser: PropTypes.bool,
  dispatch: PropTypes.func,
  featureState: PropTypes.object
};

const mapStateToProps = (reduxState) => {
  const {
    rootReducer: {
      userSessionReducer: { is_user_authenticating, is_user_authenticated, user_session },
      userStatusReducer,
      newUserFlowReducer: { new_onboarding_enabled_users: isNewUIEnabled },
      niumForexReducer: { isNiumForexUser }
    }
  } = reduxState;

  return {
    is_user_authenticating,
    is_user_authenticated,
    userStatusReducer,
    user_session,
    isNewUIEnabled,
    isNiumForexUser
  };
};

export default connect(mapStateToProps, {
  showMessage,
  authenticateUser,
  setIsNewuser,
  setIsEuRegion,
  dispatch: (action) => action
})(AuthRoute);
